import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../store';
import { updateUser } from '../../../store/slices/authSlice';
import { Address } from '../../../types/auth.types';
import './Addresses.css';

const Addresses: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<Partial<Address>>({
    name: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    pincode: '',
    isDefault: false
  });

  // Mock addresses if user doesn't exist or has none
  const addresses = user?.addresses || [
    {
      id: 'addr-1',
      name: 'John Doe',
      phone: '+91 9876543210',
      street: '123 Tech Park, Phase 1',
      city: 'Gurugram',
      state: 'Haryana',
      pincode: '122018',
      isDefault: true
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const newAddress: Address = {
      id: `addr-${Date.now()}`,
      name: formData.name || '',
      phone: formData.phone || '',
      street: formData.street || '',
      city: formData.city || '',
      state: formData.state || '',
      pincode: formData.pincode || '',
      isDefault: formData.isDefault || false
    };

    let updatedAddresses = [...addresses];
    
    // If setting as default, remove default from others
    if (newAddress.isDefault) {
      updatedAddresses = updatedAddresses.map(addr => ({ ...addr, isDefault: false }));
    }

    updatedAddresses.push(newAddress);

    if (user) {
      dispatch(updateUser({ addresses: updatedAddresses }));
    }
    
    setShowForm(false);
    setFormData({ name: '', phone: '', street: '', city: '', state: '', pincode: '', isDefault: false });
  };

  const setAsDefault = (id: string) => {
    const updatedAddresses = addresses.map(addr => ({
      ...addr,
      isDefault: addr.id === id
    }));
    if (user) {
      dispatch(updateUser({ addresses: updatedAddresses }));
    }
  };

  const deleteAddress = (id: string) => {
    const updatedAddresses = addresses.filter(addr => addr.id !== id);
    if (user) {
      dispatch(updateUser({ addresses: updatedAddresses }));
    }
  };

  return (
    <div className="addresses-page">
      <div className="addresses-header">
        <h2>My Addresses</h2>
        {!showForm && (
          <button className="btn-add-new" onClick={() => setShowForm(true)}>
            + Add New Address
          </button>
        )}
      </div>

      {showForm && (
        <form className="address-form" onSubmit={handleSave}>
          <h3 className="form-title">Add New Address</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Full Name</label>
              <input type="text" name="name" value={formData.name} onChange={handleInputChange} required />
            </div>
            <div className="form-group">
              <label>Phone Number</label>
              <input type="text" name="phone" value={formData.phone} onChange={handleInputChange} required />
            </div>
            <div className="form-group full-width">
              <label>Street Address</label>
              <input type="text" name="street" value={formData.street} onChange={handleInputChange} required />
            </div>
            <div className="form-group">
              <label>City</label>
              <input type="text" name="city" value={formData.city} onChange={handleInputChange} required />
            </div>
            <div className="form-group">
              <label>State</label>
              <input type="text" name="state" value={formData.state} onChange={handleInputChange} required />
            </div>
            <div className="form-group">
              <label>Pincode</label>
              <input type="text" name="pincode" value={formData.pincode} onChange={handleInputChange} required />
            </div>
            <div className="form-group full-width checkbox-group">
              <label>
                <input type="checkbox" name="isDefault" checked={formData.isDefault} onChange={handleInputChange} />
                Set as default address
              </label>
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="btn-save">Save Address</button>
          </div>
        </form>
      )}

      {!showForm && (
        <div className="addresses-grid">
          {addresses.length === 0 ? (
            <div className="no-addresses">You haven't added any addresses yet.</div>
          ) : (
            addresses.map(addr => (
              <div key={addr.id} className={`address-card ${addr.isDefault ? 'is-default' : ''}`}>
                {addr.isDefault && <span className="default-badge">Default</span>}
                <div className="address-card-header">
                  <h4>{addr.name}</h4>
                  <span className="phone">{addr.phone}</span>
                </div>
                <div className="address-card-body">
                  <p>{addr.street}</p>
                  <p>{addr.city}, {addr.state} {addr.pincode}</p>
                </div>
                <div className="address-card-footer">
                  {!addr.isDefault && (
                    <button className="btn-text" onClick={() => setAsDefault(addr.id)}>Set as Default</button>
                  )}
                  <div className="action-links">
                    <button className="btn-text edit">Edit</button>
                    <button className="btn-text delete" onClick={() => deleteAddress(addr.id)}>Delete</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Addresses;
